import React, { useMemo } from 'react';

import {
  Divider,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightElement,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Text,
  useColorModeValue,
} from '@ecoinvent/ui';
import { BiSearch, BiX } from 'react-icons/bi';
import { useParams } from 'react-router';

import AppCard from 'components/UI/AppCard';
import NoResultCard from 'components/UI/NoResultCard';
import useConsumingActivities from 'hooks/data/datasets/consumingActivities/useConsumingActivities';

import ConsumingActivityRow from './ConsumingActivityRow';
import { compoundActivityName } from './utils';

const ConsumingActivities = () => {
  const { spold_id } = useParams();

  const [filterString, setFilterString] = React.useState('');
  const grayTextColor = useColorModeValue('gray.500', 'gray.400');
  const { data, isLoading } = useConsumingActivities(spold_id);

  const activitiesToRender = useMemo(() => {
    return data?.consuming_activities.filter((item) =>
      compoundActivityName(item).toLowerCase().includes(filterString.toLowerCase())
    );
  }, [data, filterString]);

  return (
    <AppCard>
      <Stack spacing={5}>
        <Stack>
          <Skeleton isLoaded={!isLoading} alignSelf={'flex-start'}>
            <Heading as="h4" fontSize={'xl'}>
              Consuming Activities
            </Heading>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} alignSelf={'flex-start'}>
            <Text>Activities which depend upon this activity, and use it as an input for LCA calculations.</Text>
          </Skeleton>
        </Stack>
        <Stack spacing={1}>
          <Skeleton isLoaded={!isLoading}>
            <InputGroup>
              <InputLeftAddon rounded={'md'}>
                <Icon as={BiSearch} />
              </InputLeftAddon>
              <Input
                rounded={'md'}
                value={filterString}
                onChange={(e) => setFilterString(e.target.value)}
                placeholder="Filter activities..."
              />
              {filterString && (
                <InputRightElement>
                  <IconButton
                    size="sm"
                    icon={<Icon as={BiX} />}
                    variant="ghost"
                    aria-label={'clear input'}
                    color="gray.800"
                    onClick={() => setFilterString('')}
                  />
                </InputRightElement>
              )}
            </InputGroup>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} alignSelf={'flex-start'}>
            <Text fontSize={'xs'} color={grayTextColor}>{`${
              activitiesToRender?.length ? `Showing ${activitiesToRender.length} of ` : ''
            }${data?.total} total consuming activities`}</Text>
          </Skeleton>
        </Stack>
        <Stack spacing={1} w="100%">
          {activitiesToRender?.length ? (
            activitiesToRender.map((activity, index) => {
              return (
                <Stack key={activity.spold_id}>
                  <ConsumingActivityRow activity={activity} />
                  {index < activitiesToRender.length - 1 && <Divider />}
                </Stack>
              );
            })
          ) : (
            <Skeleton isLoaded={!isLoading}>
              <Table>
                <Tbody>
                  <NoResultCard>
                    {filterString ? 'No consuming activities match your search.' : 'No Consuming Activities'}
                  </NoResultCard>
                </Tbody>
              </Table>
            </Skeleton>
          )}
        </Stack>
      </Stack>
    </AppCard>
  );
};

export default ConsumingActivities;
